import { ReactNode, useState } from "react";
import clsx from "clsx";

import {
  Bars3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

interface SortableListProps<T> {
  items: T[];
  getKey: (item: T) => string;
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  emptyText?: string;
}

/**
 * A small drag-and-drop list built on the native HTML5 drag events. The dashboard has no
 * drag-and-drop library and a single vertical list does not justify adding one; the up/down
 * buttons cover keyboard users, which is the main thing a library would have bought us.
 */
export default function SortableList<T>({
  items,
  getKey,
  onReorder,
  renderItem,
  emptyText = "Nothing here yet.",
}: SortableListProps<T>) {
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  const handleDrop = (targetIndex: number) => {
    if (draggingKey === null) return;

    const fromIndex = items.findIndex((item) => getKey(item) === draggingKey);
    move(fromIndex, targetIndex);

    setDraggingKey(null);
    setOverKey(null);
  };

  if (items.length === 0) {
    return <p className="py-4 text-sm text-gray-500">{emptyText}</p>;
  }

  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded">
      {items.map((item, index) => {
        const key = getKey(item);

        return (
          <li
            key={key}
            draggable
            onDragStart={() => setDraggingKey(key)}
            onDragEnd={() => {
              setDraggingKey(null);
              setOverKey(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOverKey(key);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(index);
            }}
            className={clsx(
              "flex items-center gap-2 px-2 py-1.5 bg-white",
              draggingKey === key && "opacity-40",
              overKey === key && draggingKey !== key && "bg-orange-50",
            )}
          >
            <Bars3Icon
              className="h-4 w-4 shrink-0 text-gray-400 cursor-grab active:cursor-grabbing"
              aria-hidden="true"
            />
            <span className="w-6 shrink-0 text-xs text-gray-500 tabular-nums">
              {index + 1}
            </span>

            <div className="flex-1 min-w-0">{renderItem(item, index)}</div>

            <div className="flex shrink-0 flex-col">
              <button
                type="button"
                aria-label="Move up"
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
                className="text-gray-400 hover:text-orange-600 disabled:opacity-25 disabled:hover:text-gray-400"
              >
                <ChevronUpIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Move down"
                disabled={index === items.length - 1}
                onClick={() => move(index, index + 1)}
                className="text-gray-400 hover:text-orange-600 disabled:opacity-25 disabled:hover:text-gray-400"
              >
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
